"use client";

import clsx from "clsx";
import { useReducedMotion } from "framer-motion";
import { useEffect, useId, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import GailConversationPanel from "@/components/chat/GailConversationPanel";
import Button from "@/components/library/Button";
import { ArtifactPanelProvider } from "@/contexts/chat/ArtifactPanelContext";
import type { LauncherPrompt } from "@/contexts/chat/GailLauncherContext";
import { usePageContext } from "@/contexts/chat/PageContextContext";
import { useBrandConfig } from "@/hooks/useBrandConfig";
import { useOrg } from "@/hooks/useOrg";
import { useResizablePanel } from "@/hooks/useResizablePanel";
import useScreenSize from "@/hooks/useScreenSize";
import { GetGailGPTBrandName } from "@/utils/chat";
import {
  GAIL_ASSISTANT_PANEL_CLOSE_MS,
  GAIL_ASSISTANT_PANEL_EASE_VAR,
  GAIL_ASSISTANT_PANEL_OPEN_MS,
  GAIL_ASSISTANT_PANEL_TIMING,
  GAIL_ASSISTANT_PANEL_WIDTH_VAR,
} from "@/utils/chat/gailGptLauncher";

const MIN_PANEL_WIDTH = 340;
/** Reduced motion swaps the slide for a short cross-fade. */
const REDUCED_MOTION_FADE_MS = 160;

const panelMotionStyle = (open: boolean, reducedMotion: boolean) =>
  reducedMotion
    ? { opacity: open ? 1 : 0, transition: `opacity ${REDUCED_MOTION_FADE_MS}ms ease` }
    : {
      transform: open ? "translateX(0%)" : "translateX(100%)",
      transition: `transform ${open ? GAIL_ASSISTANT_PANEL_OPEN_MS : GAIL_ASSISTANT_PANEL_CLOSE_MS}ms ${GAIL_ASSISTANT_PANEL_TIMING}`,
    };

type GailAssistantSheetProps = {
  id?: string;
  triggerRef?: RefObject<HTMLButtonElement | null>;
  open: boolean;
  /** Keyboard interactions settle immediately, including Escape and shortcut reversals. */
  instantMotion?: boolean;
  onClose: () => void;
  conversationId: string | null;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  /** First message for a new chat, sent or prefilled once the welcome is ready. */
  initialPrompt?: LauncherPrompt | null;
  onInitialPromptHandled?: () => void;
};

/** Shared right-hand conversation sheet for regular pages and workflow builders. */
export default function GailAssistantSheet({
  id, triggerRef, open, instantMotion = false, onClose, conversationId, onSelectConversation, onNewChat,
  initialPrompt, onInitialPromptHandled,
}: GailAssistantSheetProps) {
  const { ui } = useOrg();
  const { config: brandConfig } = useBrandConfig(ui.brand);
  const { setAssistantOverlayOpen } = usePageContext();
  const { isMobile } = useScreenSize();
  const { t } = useTranslation("ask");
  const { t: rt } = useTranslation("routes");
  const [hasOpened, setHasOpened] = useState(open);
  const generatedPanelId = useId();
  const panelId = id ?? generatedPanelId;
  const panelRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const [reviewOpen, setReviewOpen] = useState(false);
  const conversationPanel = useResizablePanel({
    minWidth: MIN_PANEL_WIDTH,
    maxViewportFraction: 0.46,
    defaultWidth: 400,
    minCounterpartWidth: 520,
    anchor: "right",
  });
  // Review needs room for comparisons; returning to chat restores its chosen width.
  const reviewPanel = useResizablePanel({
    minWidth: MIN_PANEL_WIDTH,
    maxViewportFraction: 0.55,
    defaultWidth: 640,
    minCounterpartWidth: 520,
    anchor: "right",
  });
  const {
    width: panelWidth,
    maxWidth,
    isDragging,
    handlePointerDown,
    handleKeyDown,
  } = reviewOpen ? reviewPanel : conversationPanel;

  const brandName = GetGailGPTBrandName(ui.brand, rt, brandConfig);
  const reserveWidth = open && !isMobile;

  useEffect(() => {
    if (open) setHasOpened(true);
  }, [open]);

  useLayoutEffect(() => {
    const show = open && !isMobile;
    setAssistantOverlayOpen(show);
    return () => setAssistantOverlayOpen(false);
  }, [open, isMobile, setAssistantOverlayOpen]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    // Easing the page's width would lay it out again on every frame, so it
    // settles in one pass while the sheet itself slides on `transform`. The
    // duration is zeroed for exactly the frame the width changes, then handed
    // back so the user's own sidebar toggle keeps its animation.
    root.style.setProperty(GAIL_ASSISTANT_PANEL_EASE_VAR, "0ms");
    root.style.setProperty(
      GAIL_ASSISTANT_PANEL_WIDTH_VAR,
      reserveWidth ? `${Math.round(panelWidth)}px` : "0px"
    );
    // Two frames: a single rAF runs before the first style recalc, which would
    // pick up the restored duration together with the new width.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => root.style.removeProperty(GAIL_ASSISTANT_PANEL_EASE_VAR));
    });
    return () => cancelAnimationFrame(frame);
  }, [reserveWidth, panelWidth]);

  useLayoutEffect(() => {
    return () => {
      document.documentElement.style.removeProperty(GAIL_ASSISTANT_PANEL_WIDTH_VAR);
      document.documentElement.style.removeProperty(GAIL_ASSISTANT_PANEL_EASE_VAR);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      if (returnFocusRef.current) {
        (triggerRef?.current ?? returnFocusRef.current)?.focus({ preventScroll: true });
      }
      return;
    }
    const panel = panelRef.current;
    if (!panel) return;
    if (document.activeElement instanceof HTMLElement) returnFocusRef.current = document.activeElement;
    // Typing is the next step, so land in the composer. Phones keep focus on
    // the panel: an on-screen keyboard over a sheet that is still sliding in
    // is jarring, and iOS refuses programmatic focus outside a gesture anyway.
    const focusComposer = () => {
      if (isMobile) return false;
      const composer = panel.querySelector<HTMLTextAreaElement>("textarea:not(:disabled)");
      composer?.focus({ preventScroll: true });
      return Boolean(composer);
    };
    if (focusComposer()) return;
    panel.focus({ preventScroll: true });
    // The composer stays disabled while assistants load; move in once it
    // enables, unless the user has already put focus somewhere else.
    const observer = new MutationObserver(() => {
      if (document.activeElement !== panel || focusComposer()) observer.disconnect();
    });
    observer.observe(panel, { subtree: true, childList: true, attributes: true, attributeFilter: ["disabled"] });
    return () => observer.disconnect();
  }, [open, triggerRef, isMobile]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // The sheet doesn't block the page, so Escape only closes it when the
      // keystroke comes from inside. Portalled menus and page dialogs live
      // outside the panel and handle their own Escape.
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (event.target instanceof Node && panelRef.current?.contains(event.target)) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  return (
    <aside
      ref={panelRef}
      id={panelId}
      tabIndex={-1}
      inert={!open}
      aria-hidden={!open}
      aria-label={brandName}
      style={{
        width: isMobile ? "100%" : `${panelWidth}px`,
        ...panelMotionStyle(open, Boolean(prefersReducedMotion)),
        ...(instantMotion && { transition: "none" }),
      }}
      className={clsx(
        "fixed inset-y-0 right-0 z-40 h-dvh overflow-hidden outline-none",
        !open && "pointer-events-none"
      )}
    >
      <div
        className="relative flex h-dvh w-full flex-col border-l border-border bg-panel_secondary"
      >
        {!isMobile && (
          <Button
            type="none"
            onClick={() => {}}
            role="slider"
            aria-orientation="vertical"
            aria-label={t("dock.resize")}
            aria-valuemin={MIN_PANEL_WIDTH}
            aria-valuemax={Math.round(Math.max(MIN_PANEL_WIDTH, maxWidth))}
            aria-valuenow={Math.round(panelWidth)}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
            className="group absolute inset-y-0 left-0 z-10 w-3 cursor-col-resize touch-none !m-0 !border-0 !bg-transparent !p-0 focus:outline-none"
          >
            <span
              aria-hidden
              className={clsx(
                "absolute inset-y-0 left-0 w-0.5 transition-colors",
                "group-hover:bg-primary/40 group-focus-visible:bg-primary/50",
                isDragging && "bg-primary/50"
              )}
            />
            {/* A grip makes the edge read as draggable before anyone reads the hint. */}
            <span
              aria-hidden
              className={clsx(
                "absolute left-1 top-1/2 h-10 w-1 -translate-y-1/2 rounded-full bg-primary/50 opacity-0 transition-opacity",
                "group-hover:opacity-100 group-focus-visible:opacity-100",
                isDragging && "opacity-100"
              )}
            />
          </Button>
        )}
        <div className="relative h-full min-h-0">
          {(hasOpened || open) && (
            <ArtifactPanelProvider key={conversationId ?? "new-chat"} coversConversation>
              <GailConversationPanel
                chrome="sheet"
                conversationId={conversationId}
                onSelectConversation={onSelectConversation}
                onClose={onClose}
                onNewChat={onNewChat}
                overlayArtifacts
                onReviewOpenChange={setReviewOpen}
                initialPrompt={initialPrompt}
                onInitialPromptHandled={onInitialPromptHandled}
              />
            </ArtifactPanelProvider>
          )}
        </div>
      </div>
    </aside>
  );
}
